import React, { useState, useMemo } from 'react';
import { Package, Plus, Settings, ChevronRight, Check, DollarSign, CreditCard, Pencil, X } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';
import BundleModal from '../components/BundleModal';

const BundlesSection = () => {
  const {
    bundles,
    subscriptions,
    refData,
    expandedSections,
    saving,
    toggleSection,
    isSectionDirty,
    loadProfile,
    updateBundle,
  } = useProfile();

  const isExpanded = expandedSections.includes('bundles');
  const isDirty = isSectionDirty('bundles');

  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [selectedBundleId, setSelectedBundleId] = useState(null);
  const [showAddBundle, setShowAddBundle] = useState(false);
  const [editingCostId, setEditingCostId] = useState(null);
  const [editingCostValue, setEditingCostValue] = useState('');

  // Helper to get service names from bundle_services array
  const getServiceNames = (bundle) => {
    if (!bundle) return [];
    // First check if bundle has joined 'bundles' data with bundle_services
    if (bundle.bundles?.bundle_services && Array.isArray(bundle.bundles.bundle_services)) {
      return bundle.bundles.bundle_services.map(bs => bs.service_name);
    }
    // Then check bundle_services directly (for reference data)
    if (bundle.bundle_services && Array.isArray(bundle.bundle_services)) {
      return bundle.bundle_services.map(bs => bs.service_name);
    }
    // Fallback for included_services if it exists
    if (bundle.included_services && Array.isArray(bundle.included_services)) {
      return bundle.included_services.map(s => typeof s === 'string' ? s : s.service_name);
    }
    return [];
  };

  // Helper to get retail price for a service at a specific tier
  const getServiceRetailPrice = (serviceName, tierName) => {
    const serviceRef = refData?.streamingServices?.find(s => s.service_name === serviceName);
    if (!serviceRef || !serviceRef.tiers) return 0;
    
    // Find matching tier
    const tier = serviceRef.tiers.find(t => t.tier_name === tierName);
    if (tier) return tier.monthly_cost || 0;
    
    // Fallback to first tier (usually cheapest)
    return serviceRef.tiers[0]?.monthly_cost || 0;
  };

  // Enrich bundles with reference data
  const enrichedBundles = useMemo(() => {
    console.log('BundlesSection received bundles:', bundles);
    
    return (bundles || []).map(bundle => {
      // Get bundle reference data - first from joined data, then from refData
      const joinedBundleData = bundle.bundles; // This comes from the Supabase join
      const bundleRef = refData?.bundles?.find(b => b.bundle_code === bundle.bundle_code);
      
      // Get bundle name from joined data or fallback to refData
      const bundleName = joinedBundleData?.bundle_name || bundleRef?.bundle_name || bundle.bundle_code;
      
      // Get included services with tier info for proper pricing
      const bundleServices = joinedBundleData?.bundle_services || bundleRef?.bundle_services || [];
      const includedServices = bundleServices.map(bs => {
        const retailPrice = getServiceRetailPrice(bs.service_name, bs.included_tier);
        return {
          name: bs.service_name,
          tier: bs.included_tier,
          retailPrice,
        };
      });
      
      // Calculate savings
      const retailTotal = includedServices.reduce((sum, s) => sum + s.retailPrice, 0);
      const bundlePrice = bundle.monthly_cost || joinedBundleData?.monthly_cost || bundleRef?.monthly_cost || 0;
      const savings = retailTotal - bundlePrice;
      
      return {
        ...bundle,
        bundleName,
        includedServices,
        retailTotal,
        bundlePrice,
        savings,
        savingsPercent: retailTotal > 0 ? Math.round((savings / retailTotal) * 100) : 0,
      };
    });
  }, [bundles, refData]);

  // Summary text
  const summary = useMemo(() => {
    if (enrichedBundles.length === 0) return 'No bundles';
    const totalSavings = enrichedBundles.reduce((sum, b) => sum + Math.max(0, b.savings), 0);
    return `${enrichedBundles.length} bundle${enrichedBundles.length > 1 ? 's' : ''}${totalSavings > 0 ? ` • Saving $${totalSavings.toFixed(2)}/mo` : ''}`;
  }, [enrichedBundles]);

  const handleOpenBundleModal = (bundleId) => {
    setSelectedBundleId(bundleId);
    setBundleModalOpen(true);
  };

  // Handle saving the actual cost for a bundle
  const handleSaveBundleCost = async (bundleId, newCost) => {
    try {
      console.log('handleSaveBundleCost called:', { bundleId, newCost });
      const costValue = parseFloat(newCost) || 0;
      console.log('Parsed cost value:', costValue);
      await updateBundle(bundleId, { monthly_cost: costValue });
      console.log('updateBundle completed');
      setEditingCostId(null);
    } catch (error) {
      console.error('Error saving bundle cost:', error);
    }
  };

  // Available bundles to add (not already subscribed)
  const availableBundles = useMemo(() => {
    const existingCodes = new Set(bundles?.map(b => b.bundle_code) || []);
    return (refData?.bundles || []).filter(b => !existingCodes.has(b.bundle_code));
  }, [bundles, refData]);

  return (
    <SectionWrapper
      title="Bundles"
      icon={Package}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('bundles')}
      isDirty={isDirty}
      isSaving={saving}
      summary={summary}
      badge={enrichedBundles.length > 0 ? enrichedBundles.length : null}
    >
      <div className="space-y-4">
        {/* Bundle List */}
        {enrichedBundles.map(bundle => (
          <div 
            key={bundle.id} 
            className="border border-gray-200 rounded-lg overflow-hidden hover:border-purple-300 transition-colors"
          >
            {/* Bundle Header */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{bundle.bundleName}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {bundle.includedServices.length > 0 
                      ? bundle.includedServices.map(s => s.name).join(' + ')
                      : 'Multiple services included'}
                  </p>
                </div>
                <div className="text-right">
                  {editingCostId === bundle.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingCostValue}
                        onChange={(e) => setEditingCostValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveBundleCost(bundle.id, editingCostValue);
                          } else if (e.key === 'Escape') {
                            setEditingCostId(null);
                          }
                        }}
                        className="w-20 px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveBundleCost(bundle.id, editingCostValue)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Save"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingCostId(null)}
                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className="font-semibold text-gray-900">${bundle.bundlePrice.toFixed(2)}/mo</p>
                      <button
                        onClick={() => {
                          setEditingCostId(bundle.id);
                          setEditingCostValue(bundle.bundlePrice.toFixed(2));
                        }}
                        className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                        title="Edit what you pay"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {bundle.savings > 0 && !editingCostId && (
                    <p className="text-sm text-green-600">
                      Saving ${bundle.savings.toFixed(2)}/mo
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              {bundle.payment_method === 'credit_card_perk' && (
                <div className="mt-3 flex items-center gap-2 text-sm text-blue-600">
                  <CreditCard className="w-4 h-4" />
                  <span>Paid via {bundle.payment_source}</span>
                  {bundle.credit_applied > 0 && (
                    <span className="text-gray-500">(${parseFloat(bundle.credit_applied).toFixed(2)} credit applied)</span>
                  )}
                </div>
              )}
              {bundle.payment_method === 'paid' && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span>Direct payment</span>
                  {bundle.credit_applied > 0 && (
                    <span className="text-green-600">(${parseFloat(bundle.credit_applied).toFixed(2)} credit applied)</span>
                  )}
                </div>
              )}

              {/* Manage Button */}
              <button
                onClick={() => handleOpenBundleModal(bundle.id)}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                Manage Bundle
              </button>
            </div>

            {/* Included Services Preview */}
            <div className="p-3 bg-white border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Included services:</p>
              <div className="flex flex-wrap gap-2">
                {bundle.includedServices.length > 0 ? (
                  bundle.includedServices.map(service => (
                    <span 
                      key={service.name}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                    >
                      <Check className="w-3 h-3 text-green-500" />
                      {service.name}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-400">Service details loading...</span>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Empty State */}
        {enrichedBundles.length === 0 && !showAddBundle && (
          <div className="text-center py-6 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No bundles yet</p>
            <p className="text-xs mt-1">Bundles can save you money by combining multiple services</p>
          </div>
        )}

        {/* Add Bundle Button */}
        {!showAddBundle && (
          <button
            onClick={() => setShowAddBundle(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 hover:border-purple-400 text-gray-600 hover:text-purple-600 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Bundle
          </button>
        )}

        {/* Add Bundle Form */}
        {showAddBundle && (
          <AddBundleForm 
            availableBundles={availableBundles}
            refData={refData}
            onClose={() => setShowAddBundle(false)}
          />
        )}
      </div>

      {/* Bundle Modal */}
      <BundleModal
        isOpen={bundleModalOpen}
        onClose={() => {
          setBundleModalOpen(false);
          setSelectedBundleId(null);
        }}
        bundleId={selectedBundleId}
        onCreditReallocationNeeded={() => {
          // Expand the credit cards section so user can reallocate
          if (!expandedSections.includes('creditCards')) {
            toggleSection('creditCards');
          }
        }}
      />
    </SectionWrapper>
  );
};

// Add Bundle Form Component
const AddBundleForm = ({ availableBundles, refData, onClose }) => {
  const { saveBundle, saveSubscription, loadProfile, subscriptions, deleteSubscription } = useProfile();
  const [selectedBundleCode, setSelectedBundleCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paid');
  const [actualCost, setActualCost] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Conflict resolution state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflicts, setConflicts] = useState({ covered: [], notCovered: [] });
  const [conflictDecisions, setConflictDecisions] = useState({});

  const selectedBundle = availableBundles.find(b => b.bundle_code === selectedBundleCode);

  // Update actual cost when bundle selection changes
  React.useEffect(() => {
    if (selectedBundle) {
      setActualCost(selectedBundle.monthly_cost?.toFixed(2) || '');
    }
  }, [selectedBundle]);

  // Helper to get service names from bundle_services array
  const getServiceNames = (bundle) => {
    if (!bundle) return [];
    if (bundle.bundle_services && Array.isArray(bundle.bundle_services)) {
      return bundle.bundle_services.map(bs => bs.service_name);
    }
    if (bundle.included_services && Array.isArray(bundle.included_services)) {
      return bundle.included_services.map(s => typeof s === 'string' ? s : s.service_name);
    }
    return [];
  };

  // Check for conflicts when bundle is selected
  const detectConflicts = () => {
    if (!selectedBundle) return { covered: [], notCovered: [] };
    
    const bundleServices = getServiceNames(selectedBundle);
    const existingIndividualSubs = subscriptions.filter(sub => 
      sub.payment_method !== 'bundle' && 
      sub.payment_method !== 'perk' &&
      sub.payment_method !== 'free'
    );
    
    // Services that ARE in the bundle and user has individually
    const covered = existingIndividualSubs.filter(sub => 
      bundleServices.some(bs => bs.toLowerCase() === sub.service_name.toLowerCase())
    );
    
    // Services user has that are NOT in this bundle but ARE in related bundles
    // e.g., if user has ESPN+ but is getting Disney Duo (which doesn't include ESPN+)
    const notCovered = existingIndividualSubs.filter(sub => {
      const isInThisBundle = bundleServices.some(bs => 
        bs.toLowerCase() === sub.service_name.toLowerCase()
      );
      if (isInThisBundle) return false;
      
      // Check if this service is in a related/larger bundle
      const relatedBundles = availableBundles.filter(b => {
        const services = getServiceNames(b);
        return services.some(s => s.toLowerCase() === sub.service_name.toLowerCase()) &&
               services.some(s => bundleServices.some(bs => bs.toLowerCase() === s.toLowerCase()));
      });
      
      return relatedBundles.length > 0;
    });
    
    return { covered, notCovered };
  };

  const handleAddClick = () => {
    if (!selectedBundle) return;
    
    const detected = detectConflicts();
    setConflicts(detected);
    
    if (detected.covered.length > 0 || detected.notCovered.length > 0) {
      // Initialize decisions - default to 'remove' for covered, 'keep' for not covered
      const decisions = {};
      detected.covered.forEach(sub => {
        decisions[sub.id] = 'remove'; // Will be converted to bundle
      });
      detected.notCovered.forEach(sub => {
        decisions[sub.id] = 'keep'; // Default to keeping
      });
      setConflictDecisions(decisions);
      setShowConflictModal(true);
    } else {
      // No conflicts, proceed directly
      handleAdd();
    }
  };

  const handleAdd = async () => {
    if (!selectedBundle) return;
    
    setIsSaving(true);
    try {
      const actualCostValue = parseFloat(actualCost) || selectedBundle.monthly_cost || 0;
      
      // Create bundle record
      const newBundle = {
        bundle_code: selectedBundle.bundle_code,
        monthly_cost: actualCostValue,
        payment_method: paymentMethod,
        payment_source: null,
        credit_applied: 0,
        actual_cost: actualCostValue,
      };
      
      const savedBundle = await saveBundle(newBundle);
      
      // Handle conflicts based on decisions
      for (const sub of conflicts.covered) {
        const decision = conflictDecisions[sub.id];
        if (decision === 'remove') {
          // Remove the individual subscription (will be replaced by bundle)
          await deleteSubscription(sub.id);
        }
        // If 'keep', we leave it alone (user wants both)
      }
      
      for (const sub of conflicts.notCovered) {
        const decision = conflictDecisions[sub.id];
        if (decision === 'remove') {
          await deleteSubscription(sub.id);
        }
        // 'keep' means leave it, 'upgrade' is handled by suggesting different bundle
      }
      
      // Create subscription records for included services
      const serviceNames = getServiceNames(selectedBundle);
      for (const serviceName of serviceNames) {
        // Check if user already has this and chose to keep both
        const existingSub = conflicts.covered.find(s => 
          s.service_name.toLowerCase() === serviceName.toLowerCase()
        );
        if (existingSub && conflictDecisions[existingSub.id] === 'keep') {
          continue; // Skip - user wants to keep their existing individual sub
        }
        
        const serviceRef = refData?.streamingServices?.find(s => s.service_name === serviceName);
        const newSub = {
          service_name: serviceName,
          subscription_tier: serviceRef?.default_tier || 'Standard',
          monthly_cost: 0,
          retail_cost: serviceRef?.base_price || 0,
          payment_method: 'bundle',
          payment_source: selectedBundle.bundle_code,
          actual_cost: 0,
          credit_applied: 0,
        };
        await saveSubscription(newSub);
      }
      
      await loadProfile();
      onClose();
    } catch (error) {
      console.error('Error adding bundle:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Suggest alternative bundles for not-covered services
  const getSuggestedUpgrade = (notCoveredService) => {
    const bundleServices = getServiceNames(selectedBundle);
    
    // Find bundles that include both the selected bundle's services AND this service
    const upgrades = availableBundles.filter(b => {
      if (b.bundle_code === selectedBundleCode) return false;
      const services = getServiceNames(b);
      
      // Must include ALL services from current selection
      const includesAllCurrent = bundleServices.every(bs => 
        services.some(s => s.toLowerCase() === bs.toLowerCase())
      );
      
      // Must include the not-covered service
      const includesThis = services.some(s => 
        s.toLowerCase() === notCoveredService.service_name.toLowerCase()
      );
      
      return includesAllCurrent && includesThis;
    });
    
    // Return the cheapest upgrade option
    return upgrades.sort((a, b) => a.monthly_cost - b.monthly_cost)[0];
  };

  // Group bundles by provider for easier selection
  const bundlesByProvider = useMemo(() => {
    const groups = {};
    availableBundles.forEach(bundle => {
      const provider = bundle.provider || 'Other';
      if (!groups[provider]) groups[provider] = [];
      groups[provider].push(bundle);
    });
    return groups;
  }, [availableBundles]);

  // Conflict Resolution Modal
  const ConflictModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Review Your Subscriptions</h3>
          <p className="text-sm text-gray-600 mt-1">
            You have some existing subscriptions that overlap with {selectedBundle?.bundle_name}.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Services covered by the bundle */}
          {conflicts.covered.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                Included in {selectedBundle?.bundle_name}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                These services will be part of your bundle. We'll remove your individual subscriptions.
              </p>
              <div className="space-y-2">
                {conflicts.covered.map(sub => (
                  <div key={sub.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900">{sub.service_name}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        (was ${sub.actual_cost?.toFixed(2) || sub.monthly_cost?.toFixed(2)}/mo)
                      </span>
                    </div>
                    <select
                      value={conflictDecisions[sub.id] || 'remove'}
                      onChange={(e) => setConflictDecisions(prev => ({
                        ...prev,
                        [sub.id]: e.target.value
                      }))}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="remove">Convert to bundle</option>
                      <option value="keep">Keep both (rare)</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Services NOT covered by the bundle */}
          {conflicts.notCovered.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-600" />
                Not in {selectedBundle?.bundle_name}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                These services aren't included in this bundle. What would you like to do?
              </p>
              <div className="space-y-3">
                {conflicts.notCovered.map(sub => {
                  const upgrade = getSuggestedUpgrade(sub);
                  return (
                    <div key={sub.id} className="p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium text-gray-900">{sub.service_name}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            ${sub.actual_cost?.toFixed(2) || sub.monthly_cost?.toFixed(2)}/mo
                          </span>
                        </div>
                        <select
                          value={conflictDecisions[sub.id] || 'keep'}
                          onChange={(e) => setConflictDecisions(prev => ({
                            ...prev,
                            [sub.id]: e.target.value
                          }))}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="keep">Keep separately</option>
                          <option value="remove">Cancel it</option>
                        </select>
                      </div>
                      {upgrade && (
                        <div className="mt-2 p-2 bg-white rounded border border-orange-200">
                          <p className="text-xs text-orange-700">
                            💡 Consider <strong>{upgrade.bundle_name}</strong> (${upgrade.monthly_cost}/mo) 
                            which includes {sub.service_name}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Bundle cost: <strong>${actualCost || selectedBundle?.monthly_cost}/mo</strong></p>
              {conflicts.notCovered.filter(s => conflictDecisions[s.id] === 'keep').length > 0 && (
                <p>
                  Plus: {conflicts.notCovered
                    .filter(s => conflictDecisions[s.id] === 'keep')
                    .map(s => `${s.service_name} ($${s.actual_cost?.toFixed(2) || s.monthly_cost?.toFixed(2)})`)
                    .join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={() => setShowConflictModal(false)}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => {
              setShowConflictModal(false);
              handleAdd();
            }}
            disabled={isSaving}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
          >
            {isSaving ? 'Adding...' : 'Confirm & Add Bundle'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="border-2 border-purple-200 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900">Add Bundle</h4>

        {/* Bundle Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Bundle
          </label>
          <select
            value={selectedBundleCode}
            onChange={(e) => setSelectedBundleCode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Choose a bundle...</option>
            {Object.entries(bundlesByProvider).map(([provider, providerBundles]) => (
              <optgroup key={provider} label={provider}>
                {providerBundles.map(bundle => (
                  <option key={bundle.bundle_code} value={bundle.bundle_code}>
                    {bundle.bundle_name} (${bundle.monthly_cost}/mo)
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Selected Bundle Preview */}
        {selectedBundle && (
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="font-medium text-purple-900">{selectedBundle.bundle_name}</p>
            <p className="text-sm text-purple-700 mt-1">
              Includes: {getServiceNames(selectedBundle).join(', ')}
            </p>
            <p className="text-sm text-purple-600 mt-1">
              Base price: ${selectedBundle.monthly_cost}/mo
            </p>
          </div>
        )}

        {/* Actual Cost */}
        {selectedBundle && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What you actually pay (including tax/fees)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={selectedBundle.monthly_cost?.toFixed(2)}
              />
              <span className="text-sm text-gray-500">/mo</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Enter your actual bill amount if different from base price
            </p>
          </div>
        )}

        {/* Payment Method */}
        {selectedBundle && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="paid">Pay directly</option>
              <option value="credit_card_perk">Credit card perk</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              You can allocate card credits after adding the bundle
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAddClick}
            disabled={!selectedBundleCode || isSaving}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
          >
            {isSaving ? 'Adding...' : 'Add Bundle'}
          </button>
        </div>
      </div>
      
      {/* Conflict Resolution Modal */}
      {showConflictModal && <ConflictModal />}
    </>
  );
};

export default BundlesSection;